import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { advanceReports, type AdvanceReportListItem } from '@/api/advanceReports';
import { refs } from '@/api/refs';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nowrap: React.CSSProperties = { whiteSpace: 'nowrap' };

/**
 * ч.949 (рішення власника): авансові звіти — канон списків (Норма #29,
 * Закон дизайну #1/#5): `.card` + InfiniteDocGrid (серверна пагінація/
 * сортування) + рядок фільтрів (організація, період, підзвітна особа).
 * Організація і період слідують за шапкою застосунку.
 */
export default function AdvanceReportsPage() {
  const dp = useDefaultPeriod();
  const [orgId, setOrgId] = useOrgFilter();
  const [ovrFrom, setOvrFrom] = useState<string | null>(null);
  const [ovrTo, setOvrTo] = useState<string | null>(null);
  useEffect(() => { setOvrFrom(null); setOvrTo(null); }, [dp.dateFrom, dp.dateTo]);
  const dateFromDay = ovrFrom ?? (dp.dateFrom ? dp.dateFrom.slice(0, 10) : '');
  const dateToDay = ovrTo ?? (dp.dateTo ? dp.dateTo.slice(0, 10) : '');
  const dateFrom = dateFromDay ? `${dateFromDay}T00:00:00` : undefined;
  const dateTo = dateToDay ? `${dateToDay}T23:59:59` : undefined;
  const [employeeId, setEmployeeId] = useState('');

  const navigate = useNavigate();
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const employeesQ = useQuery({ queryKey: ['employees-all'], queryFn: () => refs.employees() });
  const empName = useMemo(() => {
    const m = new Map<string, string>();
    employeesQ.data?.forEach((e) => m.set(e.id, e.name));
    return m;
  }, [employeesQ.data]);

  const { datasource, total } = useInfiniteGridDatasource<AdvanceReportListItem>(
    (limit, offset, sort) => advanceReports.list({
      organization_id: orgId || undefined,
      employee_id: employeeId || undefined,
      date_from: dateFrom, date_to: dateTo,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId, employeeId, dateFrom, dateTo],
    { attachmentsType: 'doc.advance_reports' },
  );
  const sc = useSignControlInfinite<AdvanceReportListItem>('doc.advance_reports', orgId);

  const cols = useMemo<ColDef<AdvanceReportListItem>[]>(() => [
    { field: 'document_number', headerName: '№', width: 150 },
    {
      field: 'document_date', headerName: 'Дата', width: 170,
      valueFormatter: (p) => p.value ? new Date(p.value as string).toLocaleString('uk-UA') : '',
    },
    {
      field: 'employee_id', headerName: 'Підзвітна особа', flex: 1, minWidth: 220,
      sortable: false,
      valueFormatter: (p) => empName.get(p.value as string) ?? '',
    },
    {
      field: 'total_amount', headerName: 'Сума', width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmt2(p.value as string),
    },
    {
      field: 'posted', headerName: 'Проведено', width: 130,
      valueFormatter: (p) => (p.value ? '✓ так' : 'ні'),
    },
  ], [empName]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Авансові звіти<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/advance-reports/new')}>
          + Новий АО
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap',
        marginBottom: '0.5rem', minWidth: 0 }}>
        <select value={orgId} style={{ width: 170, ...nowrap }}
          title="Організація: слідує за верхнім фільтром шапки, доки не обрано тут вручну"
          onChange={(e) => setOrgId(e.target.value)}>
          <option value="">Усі організації</option>
          {(orgListQ.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <div style={{ width: 108, ...nowrap }}>
          <DatePopover value={dateFromDay} onChange={(v) => setOvrFrom(v)} />
        </div>
        <span style={{ color: 'var(--muted)' }}>—</span>
        <div style={{ width: 108, ...nowrap }}>
          <DatePopover value={dateToDay} onChange={(v) => setOvrTo(v)} />
        </div>
        <div style={{ width: 220, ...nowrap }}>
          <SearchSelect
            items={(employeesQ.data ?? []).map((e) => ({ id: e.id, label: e.name }))}
            value={employeeId}
            onChange={(id) => setEmployeeId(id)}
            placeholder="Підзвітна особа: усі"
          />
        </div>
      </div>
      <InfiniteDocGrid<AdvanceReportListItem>
        copyRow={(r) => navigate(`/advance-reports/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => advanceReports.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="advance-reports"
        onRowDoubleClicked={(r) => navigate(`/advance-reports/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
